/* eslint-disable react/no-unused-prop-types */
import { DEFAULT_VIEW_STATE } from 'components/Map';
import { useEffect } from 'react';
import { useMap } from 'shared/map-exports';
import { useMediaQuery, useTheme } from '@mui/material';
import useSearchDrawerState from 'state/useSearchDrawerState';
import { getBounds } from '../../shared/utils';
import { LatLng } from '../../shared/types';
import { MAP_ENTITY_DRAWER_WIDTH } from '../Map/MapEntityDrawer';
import { getSearchCardMinSnapPx, SEARCH_DRAWER_WIDTH } from '../../constants';

export type UseCenterMapProps = {
  zoomIn?: boolean;
  latLng: LatLng | null;
  drawerIsOpen?: boolean;
  defaultSnapPointPct: number;
  zoom?: number;
};

const getXOffsetForDesktop = (input: {
  isLargeMediaQuery: boolean;
  drawerIsOpen: boolean;
  isSearchDrawerOpen: boolean;
}): number => {
  const { isLargeMediaQuery, drawerIsOpen, isSearchDrawerOpen } = input;
  if (!isLargeMediaQuery) {
    return 0;
  }
  if (drawerIsOpen) {
    const offsetWidth = isSearchDrawerOpen
      ? MAP_ENTITY_DRAWER_WIDTH - SEARCH_DRAWER_WIDTH
      : MAP_ENTITY_DRAWER_WIDTH;
    return -Math.round(offsetWidth / 2);
  }
  return 0;
};

const getYOffsetForMobile = (
  isLargeMediaQuery: boolean,
  drawerIsOpen: boolean,
  defaultSnapPointPct: number,
): number => {
  if (isLargeMediaQuery) {
    return 0;
  }
  if (drawerIsOpen) {
    return -Math.round((window.innerHeight * defaultSnapPointPct) / 2);
  }

  return -Math.round(getSearchCardMinSnapPx() / 2);
};

export const useCenterMap = (props: UseCenterMapProps): void => {
  const {
    drawerIsOpen = false,
    latLng,
    zoomIn,
    defaultSnapPointPct,
    zoom,
  } = props;
  const { current: map } = useMap();
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(theme.breakpoints.up('tablet'));
  const { open: isSearchDrawerOpen } = useSearchDrawerState();

  const xOffset = getXOffsetForDesktop({
    isLargeMediaQuery,
    drawerIsOpen,
    isSearchDrawerOpen,
  });
  const yOffset = getYOffsetForMobile(
    isLargeMediaQuery,
    drawerIsOpen,
    defaultSnapPointPct,
  );

  useEffect(() => {
    if (!map || !latLng) return;

    const bbounds = getBounds(latLng.lat, latLng.lng);
    if (!bbounds) return;

    const getZoom = (): number => {
      if (zoom) return zoom;
      return zoomIn
        ? Math.max(DEFAULT_VIEW_STATE.zoom, map.getZoom())
        : map.getZoom();
    };

    requestAnimationFrame(() => {
      map.fitBounds(bbounds, {
        padding: 8,
        offset: [xOffset, yOffset],
        zoom: getZoom(),
        linear: true,
      });
    });
    // zoom level changes should not trigger a map centering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomIn, latLng, map, xOffset, yOffset]);
};

export const CenterMap = (props: UseCenterMapProps): null => {
  useCenterMap(props);
  return null;
};
